import { useUser } from "@clerk/expo";
import * as ImagePicker from "expo-image-picker";
import { Plus, X } from "lucide-react-native";
import { FC, useState } from "react";

import {
  Button,
  ButtonText,
  HStack,
  Input,
  InputField,
  MediaPicker,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { Question, QuestionDraft, Rule } from "@/types/resources";
import { QuestionSuggestion } from "@/types/responses";
import {
  CarryForwardField,
  RulesEditor,
  conditionSourceQuestions,
  hasApplicableRules,
} from "../rules";
import AdvancedSettings from "./AdvancedSettings";
import QuestionField from "./QuestionField";
import QuestionTextInput from "./QuestionTextInput";

const MAX_MEDIA = 10;
const MIN_ROWS = 2;
const MAX_ROWS = 10;
const MIN_COLUMNS = 2;
const MAX_COLUMNS = 10;

interface SingleSelectMatrixQuestionEditorProps {
  allQuestions?: Question[];
  currentQuestionOrder?: number;
  disabled?: boolean;
  onChange: (question: QuestionDraft<"single_select_matrix">) => void;
  question: QuestionDraft<"single_select_matrix">;
}

const SingleSelectMatrixQuestionEditor: FC<
  SingleSelectMatrixQuestionEditorProps
> = ({
  allQuestions = [],
  currentQuestionOrder,
  disabled = false,
  onChange,
  question,
}) => {
  const { i18n } = useLocalization();
  const { user } = useUser();
  const isAdmin = user?.publicMetadata?.admin === true;
  const [isSuggesting, setIsSuggesting] = useState(false);
  const isDisabled = disabled || isSuggesting;

  const rules = question.rules || [];
  const lines = question.lines || ["", ""];
  const options = question.options || ["", ""];

  const previousConditionQuestions = conditionSourceQuestions(
    allQuestions,
    currentQuestionOrder
  );

  const handleQuestionTextChange = (text: string) => {
    onChange({ ...question, text });
  };

  const handleSuggest = (suggestion: QuestionSuggestion) => {
    onChange({
      ...question,
      lines: suggestion.lines.length >= MIN_ROWS ? suggestion.lines : lines,
      options:
        suggestion.options.length >= MIN_COLUMNS ? suggestion.options : options,
      text: suggestion.text,
    });
  };

  // Lines (rows) handlers
  const handleLineChange = (index: number, value: string) => {
    const newLines = [...lines];
    newLines[index] = value;
    onChange({ ...question, lines: newLines });
  };

  const addLine = () => {
    if (isAdmin || lines.length < MAX_ROWS) {
      onChange({ ...question, lines: [...lines, ""] });
    }
  };

  const removeLine = (index: number) => {
    if (lines.length > MIN_ROWS) {
      onChange({
        ...question,
        lines: lines.filter((_, i) => i !== index),
      });
    }
  };

  // Options (columns) handlers
  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    onChange({ ...question, options: newOptions });
  };

  const addOption = () => {
    if (isAdmin || options.length < MAX_COLUMNS) {
      onChange({ ...question, options: [...options, ""] });
    }
  };

  const removeOption = (index: number) => {
    if (options.length > MIN_COLUMNS) {
      onChange({
        ...question,
        options: options.filter((_, i) => i !== index),
      });
    }
  };

  const handleMediaChange = (
    newMedia: (ImagePicker.ImagePickerAsset | string)[]
  ) => {
    onChange({ ...question, media: newMedia });
  };

  const handleRulesChange = (newRules: Rule[]) => {
    onChange({ ...question, rules: newRules });
  };

  return (
    <MediaPicker
      isDisabled={isDisabled}
      maxItems={MAX_MEDIA}
      media={question.media}
      onMediaChange={handleMediaChange}
    >
      <VStack className="gap-6">
        <VStack className="gap-3">
          <QuestionField order={currentQuestionOrder}>
            <QuestionTextInput
              disabled={disabled}
              lines={lines}
              onChange={handleQuestionTextChange}
              onLoadingChange={setIsSuggesting}
              onSuggest={handleSuggest}
              options={options}
              text={question.text || ""}
              type={question.type}
            />
            <MediaPicker.Trigger compact height={70} width={70} />
          </QuestionField>
          <MediaPicker.Thumbnails />
          <MediaPicker.Error />
        </VStack>

        {/* Rows and Columns side by side */}
        <HStack className="gap-6 flex-col lg:flex-row">
          {/* Rows (lines) — authored manually or reused from a prior question */}
          <VStack className="gap-3 w-full lg:flex-1">
            <Text className="text-sm font-semibold text-typography-800">
              {i18n.t("questions.matrix.rows")}
            </Text>
            <CarryForwardField
              allQuestions={allQuestions}
              currentQuestionOrder={currentQuestionOrder}
              disabled={isDisabled}
              manualLabel={i18n.t("questions.carryForward.modeManualRows")}
              onChange={({ carry_forward, items }) =>
                onChange({
                  ...question,
                  carry_forward,
                  ...(items !== null ? { lines: items } : {}),
                })
              }
              reuseDisabled={question.carry_forward?.target === "options"}
              target="lines"
              value={
                question.carry_forward?.target === "lines"
                  ? question.carry_forward
                  : null
              }
            >
              <VStack className="gap-3">
                {lines.map((line, index) => (
                  <HStack key={`row-${index}`} className="items-center gap-3">
                    <Text className="text-sm text-typography-500 w-6">
                      {index + 1}.
                    </Text>
                    <Input
                      className="flex-1 border border-outline-200 rounded-xl bg-background-50"
                      isDisabled={isDisabled}
                    >
                      <InputField
                        onChangeText={(value) => handleLineChange(index, value)}
                        placeholder={i18n.t("questions.matrix.rowPlaceholder")}
                        value={line}
                      />
                    </Input>
                    {lines.length > MIN_ROWS && (
                      <Pressable
                        className="p-1.5 rounded-md hover:bg-error-50"
                        disabled={isDisabled}
                        onPress={() => removeLine(index)}
                      >
                        <X color="rgb(var(--color-typography-500))" size={16} />
                      </Pressable>
                    )}
                  </HStack>
                ))}
                {(isAdmin || lines.length < MAX_ROWS) && (
                  <Button
                    className="self-start rounded-lg border-dashed"
                    isDisabled={isDisabled}
                    onPress={addLine}
                    size="sm"
                    variant="outline"
                  >
                    <Plus color="rgb(var(--color-typography-600))" size={14} />
                    <ButtonText className="text-sm text-typography-600 ml-1">
                      {i18n.t("questions.matrix.addRow")}
                    </ButtonText>
                  </Button>
                )}
              </VStack>
            </CarryForwardField>
          </VStack>

          {/* Columns (options) — authored manually or reused from a prior
              question (e.g. carrying a brand list into the columns). */}
          <VStack className="gap-3 w-full lg:flex-1">
            <Text className="text-sm font-semibold text-typography-800">
              {i18n.t("questions.matrix.columns")}
            </Text>
            <CarryForwardField
              allQuestions={allQuestions}
              currentQuestionOrder={currentQuestionOrder}
              disabled={isDisabled}
              manualLabel={i18n.t("questions.carryForward.modeManualOptions")}
              onChange={({ carry_forward, items }) =>
                onChange({
                  ...question,
                  carry_forward,
                  ...(items !== null ? { options: items } : {}),
                })
              }
              reuseDisabled={question.carry_forward?.target === "lines"}
              target="options"
              value={
                question.carry_forward?.target === "options"
                  ? question.carry_forward
                  : null
              }
            >
              <VStack className="gap-3">
                {options.map((option, index) => (
                  <HStack key={`col-${index}`} className="items-center gap-3">
                    <Text className="text-sm text-typography-500 w-6">
                      {index + 1}.
                    </Text>
                    <Input
                      className="flex-1 border border-outline-200 rounded-xl bg-background-50"
                      isDisabled={isDisabled}
                    >
                      <InputField
                        onChangeText={(value) =>
                          handleOptionChange(index, value)
                        }
                        placeholder={i18n.t(
                          "questions.matrix.columnPlaceholder"
                        )}
                        value={option}
                      />
                    </Input>
                    {options.length > MIN_COLUMNS && (
                      <Pressable
                        className="p-1.5 rounded-md hover:bg-error-50"
                        disabled={isDisabled}
                        onPress={() => removeOption(index)}
                      >
                        <X color="rgb(var(--color-typography-500))" size={16} />
                      </Pressable>
                    )}
                  </HStack>
                ))}
                {(isAdmin || options.length < MAX_COLUMNS) && (
                  <Button
                    className="self-start rounded-lg border-dashed"
                    isDisabled={isDisabled}
                    onPress={addOption}
                    size="sm"
                    variant="outline"
                  >
                    <Plus color="rgb(var(--color-typography-600))" size={14} />
                    <ButtonText className="text-sm text-typography-600 ml-1">
                      {i18n.t("questions.matrix.addColumn")}
                    </ButtonText>
                  </Button>
                )}
              </VStack>
            </CarryForwardField>
          </VStack>
        </HStack>

        {/* Skip logic */}
        {hasApplicableRules(allQuestions, currentQuestionOrder) && (
          <AdvancedSettings defaultExpanded={rules.length > 0}>
            <RulesEditor
              allQuestions={allQuestions}
              currentQuestionOrder={currentQuestionOrder}
              disabled={isDisabled}
              onRulesChange={handleRulesChange}
              previousConditionQuestions={previousConditionQuestions}
              rules={rules}
            />
          </AdvancedSettings>
        )}
      </VStack>
    </MediaPicker>
  );
};

export default SingleSelectMatrixQuestionEditor;
